<template lang='pug'>
.article-container
  article-schema(
    v-if ='!loading'
    :title='title'
    :text-snippet='textSnippet'
    :images='[mainImage]'
    :content-type-name='contentTypeName'
    :published-at='publishedAt'
    :tags='tags'
  )
  .row(v-if='loading')
    .offset-md-0.col-md-8.offset-lg-1.col-lg-7
      .back
        .svg-icon.chevron-left
        a(@click='back')= 'Back'
    .col-12
      .article-loader
        .loader-spinner
  .row(v-else)
    .offset-md-0.col-md-8.offset-lg-1.col-lg-7.summary-container(:class='isMobile ? "card-shadow" : null')
      .back
        .svg-icon.chevron-left
        a(@click='back')= 'Back'
      .article-contents(v-for='content, index in articleContents' :key='index')
        div(:class='containerClasses(content)')
          article-title(
            v-if='content.type === "titles"'
            :title='content.attributes.content'
            :publishedAt='publishedAt'
            :contentTypeName='contentTypeName'
          )
          h3(v-if='content.type === "subtitles"' v-text='content.attributes.content')
          .video-container(v-if='content.type === "videos"')
            div(v-text='content.attributes.youtube_id')
            iframe(:src='`https://www.youtube.com/embed/${content.attributes.youtube_id}`')
          .video-container(v-if='content.type === "vudoo"')
            div(v-text='content.attributes.vudoo_id')
            iframe(:src='`https://vudoo.io/embed/${content.attributes.vudoo_id}`' allow='autoplay' allowFullScreen='true' frameborder='0')
          ProductsCarousel(
            class='clearfix mb-20'
            v-if='isProductCarousel(content)'
            :entityOptions='{}'
            :carouselOptions='{ size: "sm", sliderClass: `article-carousel-${index}`, responsive: { md: { items: 3, slideBy: 3 } } }'
            :products='filterProducts(content)'
            :variants='variants'
            :autoplay='false'
            :indexChangedCallback='pushCustomDataLayerEvent'
          )
          variant-carousel(
            class='mb-20'
            v-if='isVariantCarousel(content)'
            :carousel='{ id: index, attributes: {}, variants: filterVariants(content) }'
            :items='3'
            :slideBy='3'
            :indexChangedCallback='pushCustomDataLayerEvent'
          )
          image-collection(
            v-if='content.type === "image_collections"'
            v-bind='content'
            :images='collectionImages(content)'
          )
          listing(
            v-if='isProductListing(content)'
            :products='filterProducts(content)'
            :variants='variants'
          )
          variant-listing(
            v-if='isVariantListing(content)'
            :variants='filterVariants(content)'
          )
          .text(v-if='content.type === "texts"' v-html='content.attributes.content')
          linkable-image(v-if='isNonCollectionImage(content)' :content='convertObjToContent(content)')
          linkable-button(v-if='content.type === "buttons"' :content='convertObjToContent(content)')
      tags(v-if='tags.length > 0')
    .card-gap(v-if='isMobile')
    .col-md-4.col-lg-3.related-articles(v-if='nextArticle.id !== undefined')
      h3(v-text='"NEXT ARTICLE"')
      article-card(v-bind='nextArticle' :extraMargin='true' :leftAlign='true' :isNextArticle='true')
</template>

<script>
import { getArticleByFilters } from '@/modules/content-platform-api'
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import intersection from 'lodash/intersection'
import flatMap from 'lodash/flatMap'
import { Content } from './props'
import { fullUrl } from '@/modules/services/utilities'
import { pushDataLayerEvent } from '@/modules/services/gtm'
import ProductsCarousel from '../products/products-carousel.vue'
import VariantCarousel from '../landing-pages/four-groups/variant-carousel.vue'
import Listing from './article/contents/product-listing.vue'
import VariantListing from './article/contents/variant-listing.vue'
import ArticleCard from './article/card.vue'
import LinkableImage from './article/contents/linkable-image.vue'
import LinkableButton from './article/contents/linkable-button.vue'
import Title from './article/contents/title.vue'
import Tags from './article/contents/tags.vue'
import ImageCollection from './article/contents/image-collection.vue'
import ArticleSchema from './beautyfeed-schema.vue'
import { dynamicLoadStore } from '@/mixins/store-util'
import beautyFeed from '@/store/modules/beauty-feed'
import beautyFeedShow from '@/store/modules/beauty-feed-show'

const ARTICLE_CONTENT_TYPES = [
  'titles', 'subtitles', 'texts',
  'images', 'image_collections', 'productfeed',
  'videos', 'vudoo', 'buttons'
]

export default {
  components: {
    ArticleCard,
    ProductsCarousel,
    ImageCollection,
    VariantCarousel,
    Listing,
    VariantListing,
    LinkableImage,
    LinkableButton,
    Tags,
    ArticleSchema,
    'article-title': Title
  },
  mixins: [
    dynamicLoadStore({ beautyFeed, beautyFeedShow })
  ],
  data () {
    return {
      previousRoute: null
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => (vm.previousRoute = from))
  },
  computed: {
    ...mapState('beautyFeedShow', {
      mainImage: state => state.mainImage,
      contentTypeName: state => state.contentTypeName,
      title: state => state.title,
      textSnippet: state => state.textSnippet,
      publishedAt: state => state.publishedAt,
      articleContents: state => state.articleContents,
      products: state => state.products,
      variants: state => state.variants,
      tags: state => state.tags,
      loading: state => state.loading,
      nextArticle: state => state.nextArticle,
      pageTitle: state => state.pageTitle,
      metaDescription: state => state.metaDescription,
      imageCollectionImages: state => state.imageCollectionImages,
      images: state => state.images
    }),
    ...mapState('beautyFeed', {
      selectedFilter: state => state.selectedFilter,
      selectedTag: state => state.selectedTag,
      selectedTab: state => state.selectedTab
    }),
    isProductCarousel () {
      return content => {
        if (content.type === 'productfeed') {
          return content.attributes['display-type'].includes('Carousel') && !isEmpty(content.relationships.products)
        } else {
          return false
        }
      }
    },
    isVariantCarousel () {
      return content => {
        if (content.type === 'productfeed') {
          return content.attributes['display-type'].includes('Carousel') && !isEmpty(content.relationships.variants)
        } else {
          return false
        }
      }
    },
    isProductListing () {
      return content => {
        if (content.type === 'productfeed') {
          return content.attributes['display-type'].includes('Listing') && !isEmpty(content.relationships.products)
        } else {
          return false
        }
      }
    },
    isVariantListing () {
      return content => {
        if (content.type === 'productfeed') {
          return content.attributes['display-type'].includes('Listing') && !isEmpty(content.relationships.variants)
        } else {
          return false
        }
      }
    },
    isMobile () {
      return vueSupport.isMobile
    },
    convertObjToContent () {
      return content => new Content(content)
    },
    containerClasses () {
      return content => ['videos', 'vudoo', 'images', 'image_collections'].includes(content.type) ? 'w-100' : 'pad'
    },
    collectionImages () {
      return (collection) => {
        const imageIds = collection.relationships.images.data.map(({ id }) => id)
        return this.imageCollectionImages.filter(image => imageIds.includes(image.id))
      }
    },
    isNonCollectionImage () {
      return (image) => {
        return image.type === 'images' && this.images.map(({ id }) => id).includes(image.id)
      }
    },
    pushCustomDataLayerEvent () { // eslint-disable-line vue/return-in-computed-property
      pushDataLayerEvent('carousel.product.updated')
    }
  },
  watch: {
    '$route': 'fetchArticle'
  },
  async created () {
    this.$store.commit('beautyFeedShow/setLoading', true)
    this.fetchArticle()
  },
  methods: {
    async fetchArticle () {
      try {
        let options = {}

        if (this.selectedTag) {
          options = { tags: this.selectedTag }
        } else {
          if (this.selectedFilter !== 'All') {
            options.articleFilters = this.selectedFilter
          }
          if (this.selectedTab !== 'Home') {
            options.contentTypes = this.selectedTab
          }
        }

        const { data: { data: { attributes, relationships }, included, meta } } =
        await getArticleByFilters(
          this.$route.params.slug, I18n.acceptLanguage, {
            filter: options,
            include: { article_contents: true, tags: true }
          }
        )

        const contentTypeId = relationships.content_types.data[0].id
        const contentType = included.find(({ type, id }) => type === 'content_types' && id === contentTypeId)

        // filter out article content object
        const articleContents = included.filter(({ type }) => ARTICLE_CONTENT_TYPES.includes(type))
        const products = included.filter(({ type }) => type === 'products')
        const variants = included.filter(variant => {
          return variant.type === 'variants' && variant.relationships.product.data !== null
        })
        const tags = included.filter(({ type }) => type === 'tags')
        const imageCollections = included.filter(({ type }) => type === 'image_collections')
        const imageCollectionImageIds = flatMap(imageCollections, (ic) => ic.relationships.images.data).map(({ id }) => id)
        const allImages = included.filter(({ id, type }) => type === 'images')

        // image that exist in image collection
        const imageCollectionImages = allImages.filter(({ id }) => imageCollectionImageIds.includes(id))
        const images = allImages.filter(({ id }) => !imageCollectionImageIds.includes(id))

        const article = {
          title: attributes.title,
          textSnippet: attributes.text_snippet,
          mainImage: attributes.banner_image_url,
          publishedAt: attributes.published_at,
          slugUrl: attributes.slug,
          contentTypeName: contentType.attributes.name,
          pageTitle: attributes.page_title,
          metaDescription: attributes.meta_description
        }

        this.$store.commit('beautyFeedShow/setArticle', article)

        const nextArticleData = meta && meta.next_article || {} // eslint-disable-line no-mixed-operators
        const nextArticle = {
          id: nextArticleData['article-id'],
          title: nextArticleData['article-title'],
          textSnippet: nextArticleData['article-summary'],
          mainImage: nextArticleData['banner-image-url'],
          publishedAt: new Date(nextArticleData['published-at']),
          contentType: { name: nextArticleData['content-type'] },
          slugUrl: nextArticleData['article-slug']
        }

        this.$store.commit('beautyFeedShow/setArticleContents', articleContents)
        this.$store.commit('beautyFeedShow/setImageCollectionImages', imageCollectionImages)
        this.$store.commit('beautyFeedShow/setImages', images)
        this.$store.commit('beautyFeedShow/setProducts', products)
        this.$store.commit('beautyFeedShow/setVariants', variants)
        this.$store.commit('beautyFeedShow/setTags', tags)
        this.$store.commit('beautyFeedShow/setLoading', false)
        this.$store.commit('beautyFeedShow/setNextArticle', nextArticle)
        // SEO
        this.$store.commit('setSeoMetaTitle', this.pageTitle ? this.pageTitle : this.title)
        this.$store.commit('setSeoMetaDescription', this.metaDescription ? this.metaDescription : this.textSnippet)
        this.$store.commit('setSeoOgUrl', fullUrl(this.$route.path))
        this.$store.commit('setSeoUpdate', true)
      } catch (e) {
        console.error(e)
        this.$router.push('/beautyfeed')
        this.$store.commit('setNotification', {
          type: 'error',
          msg: this.$t('errorMessage.pageNotAvailable')
        })
      }
    },
    back () {
      this.previousRoute.path.includes('beautyfeed') ? this.$router.go(-1) : this.$router.push('/beautyfeed')
    },
    filterProducts (content) {
      const mapId = ({ id }) => id
      if (!isEmpty(content.relationships.products)) {
        const filteredIds = intersection(this.products.map(mapId), content.relationships.products.data.map(mapId))
        const filteredProducts = this.products.filter(({ id }) => filteredIds.includes(id))
        return filteredProducts
      }

      return []
    },
    filterVariants (content) {
      const mapId = ({ id }) => id
      if (!isEmpty(content.relationships.variants)) {
        const filteredIds = intersection(this.variants.map(mapId), content.relationships.variants.data.map(mapId))
        const filterddVariants = this.variants.filter(({ id }) => filteredIds.includes(id))
        return filterddVariants
      }

      return []
    }
  }
}
</script>

<style lang='scss'>
  .article-contents {
    a {
      text-decoration: underline;

      &.product-card-description {
        text-decoration: none;
      }
    }

    strong {
      @include font-weight-bold;
    }

    p {
      margin-bottom: 20px;
    }

    blockquote {
      @include font-sephora-serif-italic;
      font-size: 30px;
      position: relative;
      text-align: center;
      line-height: 36px;
      margin: 30px 20px 30px 30px;

      @include media-breakpoint-down(sm) {
        margin: 30px 5px 30px 18px;
      }
    }

    blockquote:before {
      font-size: 80px;
      position: absolute;
      top: -5px;
      left: -30px;
      content: open-quote;
    }

    blockquote:after {
      font-size: 80px;
      position: absolute;
      right: 0;
      bottom: -45px;
      content: close-quote;
    }
  }

  div[class*="col-"] {
    box-sizing: border-box;
  }
</style>

<style lang='scss' scoped>
  .svg-icon {
    width: 30px;
    height: 10px;

    @include media-breakpoint-up(md) {
      margin-left: -15px;
    }
  }

  .clearfix {
    clear: both;
  }

  .mb-20 {
    margin-bottom: 20px;
  }

  /deep/ .arrows {
    .right {
      margin-top: -20px;
    }
  }

  .article-contents {
    @include media-breakpoint-up(md) {
      font-size: 16px;
      line-height: 28px;
      letter-spacing: 0.3px;
    }
  }

  .summary-container {
    padding: 0;

    @include media-breakpoint-up(md) {
      padding: 0 15px 15px;
    }
  }

  .article-loader {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .img-container {
    margin-bottom: 27px;
  }

  .w-100 {
    width: 100%;
  }

  .pad {
    padding: 0 30px;
    @include media-breakpoint-up(md) {
      padding: 0;
    }
  }

  .header-section {
    padding: 22px 0;
    width: 100%;
  }

  .back {
    @extend .header-section;

    text-decoration: underline;
    cursor: pointer;

    @include media-breakpoint-only(xs) {
      padding: 5px 15px 22px 15px;
    }
  }

  .related-articles {
    h3 {
      padding: 22px 15px;
      width: 100%;
    }

    @include media-breakpoint-up(md) {
      h3 {
        padding: 22px 0px;
        margin-bottom: 10px;
      }
    }
  }

  .res-image {
    width: 100%;
    height: auto;
  }

  .title {
    width: 100%;
    font-size: 24px;
    line-height: 29px;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .filter-name {
    width: 100%;

    display: block;
    font-size: 13px;
    font-weight: bold;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: #C9362F;
  }

  .text-snippet {
    text-align :center;
    word-wrap: break-word;
    color: $grey-dark;
  }

  .dim {
    margin-top: 4px;
    color: #999999;
  }

  .article-summary {
    margin-bottom: 10px;
    padding: 0 25px;

    @include media-breakpoint-up(md) {
      padding: 0;
      margin-bottom: 16px;
    }
  }

  .title, .text-snippet, .filter-name, .dim {
    text-align: left;
  }

  .text {
    margin-bottom: 10px;
  }

  .card-shadow {
    box-shadow: 0 2px 0px #A2A2A2;
  }

  .card-gap {
    height: 12px;
    width: 100%;
    background: $grey-light;
  }

  .extra-padding {
    padding-left: 15px;
    padding-right: 15px;
  }

  .video-container {
    overflow: hidden;
    padding-top: 56.25%;
    position: relative;

    margin: 15px 0;
  }

  .video-container iframe {
    border: 0;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }

  @include media-breakpoint-up(md) {
    .dim {
      margin-top: 8px;
    }
  }
</style>
